import { Suspense, useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useParams } from 'react-router'

import { CommandPalette } from './CommandPalette'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'

import { Dialog, Spinner } from '@/components/primitives'
import { ImpersonationBanner } from '@/features/admin/ImpersonationBanner'
import { useMyWorkspaces, useWorkspace } from '@/features/workspaces/queries'

/**
 * Commerce is opt-OUT, not opt-in.
 *
 * It used to be opt-in and nothing ever passed the flag, so Products, Orders
 * and Bookings were routes the router served and the navigation never
 * mentioned. A workspace that genuinely sells nothing sets
 * `settings.commerce_enabled` to false and the section disappears again.
 */
function useHasCommerce(workspaceSlug: string): boolean {
  const { data: workspaces } = useMyWorkspaces()
  const id = workspaces?.find((w) => w.slug === workspaceSlug)?.id
  const { data } = useWorkspace(id)
  return data?.settings['commerce_enabled'] !== false
}

const COLLAPSE_KEY = 'convo:sidebar-collapsed'
function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1'
  } catch {
    return false
  }
}

export function AppShell() {
  const { workspaceSlug = '' } = useParams<{ workspaceSlug: string }>()
  const { pathname } = useLocation()
  const hasCommerce = useHasCommerce(workspaceSlug)
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const [searchOpen, setSearchOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const mainRef = useRef<HTMLElement>(null)
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0')
    } catch {
      /* Preference only. */
    }
  }, [collapsed])
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, behavior: 'instant' })
    setMobileOpen(false)
    setSearchOpen(false)
  }, [pathname])
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (
        workspaceSlug &&
        event.key === 'k' &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault()
        setSearchOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [workspaceSlug])

  return (
    <div className="app-shell flex h-dvh overflow-hidden bg-surface-sunken">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <div className="hidden md:block">
        <Sidebar
          workspaceSlug={workspaceSlug}
          collapsed={collapsed}
          hasCommerce={hasCommerce}
          scope={workspaceSlug ? 'workspace' : 'platform'}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <ImpersonationBanner />
        <Topbar
          onToggleSidebar={() => {
            if (window.matchMedia('(max-width: 767px)').matches)
              setMobileOpen(true)
            else setCollapsed((v) => !v)
          }}
          onOpenSearch={() => setSearchOpen(true)}
        />
        <main
          id="main"
          ref={mainRef}
          tabIndex={-1}
          className="workspace-main flex-1 overflow-auto"
        >
          <Suspense
            fallback={
              <div className="grid h-full place-items-center">
                <Spinner size={24} label="Loading page" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>
      </div>
      {searchOpen && workspaceSlug && (
        <CommandPalette
          workspaceSlug={workspaceSlug}
          onClose={() => setSearchOpen(false)}
        />
      )}
      <Dialog
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        title="Your workspace"
        size="sm"
      >
        <div className="mobile-navigation">
          <Sidebar
            workspaceSlug={workspaceSlug}
            collapsed={false}
            hasCommerce={hasCommerce}
            scope={workspaceSlug ? 'workspace' : 'platform'}
          />
        </div>
      </Dialog>
    </div>
  )
}
